import { ChangeEvent, useEffect, useState } from 'react'

import SearchIcon from '@mui/icons-material/Search'
import {
  Grid,
  Input,
  Picker,
  Span,
  Button,
  Form,
  SelectForm,
  Row,
  AutoCompleteSearchTerm,
} from 'components'
import { useDebounce, useException, useLoading, useModal } from 'hooks'
import moment from 'moment'
import { IForm } from 'pages/GST/PesquisaPorContrato/types'
import { useFormContext } from 'react-hook-form'
import { IResponseTypes } from 'services/core/genericEntityGST'
import {
  CentrosServices,
  EmpresasServices,
  ICentroPlantasItens,
  IEmpresas,
  IGenericGetALlCentro,
  IPesquisaContratosGetByFilterParams
} from 'services/GST/PesquisaPorContrato'
import { ICsnUserGotBy, UserCsnService } from 'services/Supplier/Users'
import { SPACING } from 'utils'

interface IFilterProps {
  handleSubmitForm: (_params: IPesquisaContratosGetByFilterParams) => void
  isFirstGet: boolean
  areDatesValid: () => boolean
}

export const FilterTable: React.FC<IFilterProps> = ({
  handleSubmitForm,
  isFirstGet,
  areDatesValid
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    getValues,
    reset,
    setValue
  } = useFormContext<IForm>()

  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const { getException, createCustomException } = useException()

  const [centros, setCentros] = useState<ICentroPlantasItens[]>([])
  const [empresasList, setEmpresasList] = useState<IEmpresas[]>()

  const [contrapartes, setContrapartes] = useState<ICsnUserGotBy[]>([])
  const [contraparteLoading, setContraparteLoading] = useState<boolean>(false)

  const documento = watch('cdDocto')

  const handleSearchContraparte = (event: ChangeEvent<HTMLInputElement>) => {
    setContraparteLoading(true)

    UserCsnService()
      .getByNome(event.target.value)
      .then((response) => {
        setContrapartes(response.data.map((value) => value.usuario))
      })
      .finally(() => setContraparteLoading(false))
  }

  const handleGetAllCentroPlantas = (): void => {
    setLoading('loading')

    CentrosServices()
      .getAllCentroPlantas()
      .then((response) => {

        if (response?.status !== 200 || response?.data?.tipo === IResponseTypes['alert']) {
          const { tipo, detalhe, mensagem, } = response?.data

          createCustomException({
            detalhe,
            message: detalhe || mensagem || 'Ocorreu um erro ao fazer a requisição',
            tipo: IResponseTypes[tipo]
          })
        }

        setCentros(response?.data?.entity?.semValidacao
          .sort((a: IGenericGetALlCentro, b: IGenericGetALlCentro) =>
            (a?.txDescricaoCentroPlanta > b?.txDescricaoCentroPlanta) ? 1 : -1)
        )
        setLoading('idle')
      })
      .catch((e: Error) => {
        const error = getException(e)

        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: error.tipo || 'error',
        })
      })
  }

  const handleClear = () => {
    reset()
    setValue("nmContraparte", '')
    handleSubmitForm(getValues())
  }

  const handleGetEmpresas = () => {
    setLoading('loading')

    EmpresasServices()
      .getEmpresasList()
      .then((response) => {
        if (
          response?.status !== 200 ||
          response?.data?.tipo === IResponseTypes['alert']
        ) {
          const { tipo, detalhe, mensagem } = response?.data

          createCustomException({
            detalhe,
            message:
              detalhe || mensagem || 'Ocorreu um erro ao fazer a requisição',
            tipo: IResponseTypes[tipo]
          })
        }

        setEmpresasList(response?.data?.entity)
        setLoading('idle')
      })
      .catch((e: Error) => {
        const error = getException(e)

        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: error.tipo || 'error'
        })
      })
  }

  useEffect(() => {
    if (isFirstGet) {
      handleGetEmpresas()
      handleGetAllCentroPlantas()
    }
  }, [isFirstGet])

  return (
    <Form
      onSubmit={handleSubmit((data: IForm) => {
        handleSubmitForm({
          ...data,
          enviarListas: false,
          pageNumber: 1
        })
      })}
    >
      <Grid
        gridCols="2.4fr 2.4fr  2.4fr  2.4fr  2.4fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
      >
        <Input
          formValue="cdContrato"
          label="Contrato"
          placeholder="Contrato"
          control={control}
          errors={errors}
        />
        <Input
          formValue="cdDocto"
          label="CPF/CNPJ/Cód.Fornecedor"
          control={control}
          errors={errors}
          placeholder="Nº"
          maxLength={18}
          mask={
            isNaN(parseInt(documento))
              ? ['#################']
              : ['###.###.###-##', '##.###.###/####-##']
          }
        />
        <Input
          formValue="nmFornecedor"
          label="Fornecedor"
          control={control}
          errors={errors}
          placeholder="Nome do Fornecedor"
        />
        <Input
          formValue="nmContraparte"
          label="Contraparte"
          control={control}
          errors={errors}
          placeholder="Nome do Contraparte"
        />
        {/* <AutoCompleteSearchTerm
          errors={errors}
          options={contrapartes || []}
          label="Contraparte"
          placeholder="contraparte"
          onChangeText={useDebounce(handleSearchContraparte, 600)}
          optionValue="nmUsuario"
          control={control}
          loading={contraparteLoading}
          formValue="nmContraparte"
          optionLabel="nmUsuario"
        /> */}
      </Grid>
      <Grid
        gridCols="2fr 1.5fr 1fr 0.1fr 1fr 1fr 1fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing20} 0 ${SPACING.spacing10} 0`}
      >
        <SelectForm
          defaultValue={undefined}
          control={control}
          errors={errors}
          label="Empresa"
          placeholder="Selecione"
          clearSelect={true}
          formValue="empresaId"
          listOptions={
            empresasList?.map((empresa: IEmpresas) => ({
              label: empresa?.nmEmpresa,
              value: empresa?.empresaId
            })) || []
          }
        />

        <SelectForm
          defaultValue={undefined}
          control={control}
          errors={errors}
          label="Centro"
          placeholder="Selecione"
          clearSelect={true}
          formValue="centroPlantaId"
          listOptions={
            centros?.map((centro: ICentroPlantasItens) => ({
              label: centro?.txDescricaoCentroPlanta,
              value: centro?.idCentroPlanta
            })) || []
          }
        />

        <Picker
          defaultValue={null}
          formValue="dtCadastroInicial"
          maxDate={
            watch('dtCadastroFinal') ? moment(watch('dtCadastroFinal')) : null
          }
          label="Data Cadastro"
          control={control}
          errors={errors}
          type={'dayMonthYear'}
        />

        <Span text="à" marginTop={SPACING.spacing30} />

        <Picker
          defaultValue={null}
          formValue="dtCadastroFinal"
          minDate={
            watch('dtCadastroInicial')
              ? moment(watch('dtCadastroInicial'))
              : null
          }
          label="."
          control={control}
          errors={errors}
          type={'dayMonthYear'}
        />

      </Grid>
      <Row justifyContent="flex-end">
        <Button
          width="150"
          text="Limpar"
          onClick={handleClear}
          variantButton={"text"}
        />
        <div style={{ width: '1rem' }} />
        <Button
          endIcon={<SearchIcon />}
          width="150"
          disabled={!areDatesValid()}
          text="Pesquisar"
          type="submit"
        />
      </Row>
    </Form>
  )
}
